import React, { useMemo } from 'react';
import { ThemedSpinner } from '../ThemedSpinner';
import { useTranslation } from '../../localization/useAppTranslation';
import type {
  PromptSuggestionCategory,
  PromptSuggestionItem,
  PromptSuggestionsErrorInfo,
} from '../../types/promptSuggestions';
import type { PromptSuggestionsStatus } from '../../context/PromptSuggestionsContext';
import { PromptSuggestionControl } from './PromptSuggestionControl';

interface CoachSuggestionsPanelProps {
  categories: PromptSuggestionCategory[];
  suggestions: PromptSuggestionItem[];
  isLoading: boolean;
  status: PromptSuggestionsStatus;
  error: PromptSuggestionsErrorInfo | null;
  canSendSuggestion: boolean;
  onRetryLoad: () => Promise<void>;
  onUseSuggestion: (suggestion: PromptSuggestionItem) => void;
}

export const CoachSuggestionsPanel: React.FC<CoachSuggestionsPanelProps> = ({
  categories,
  suggestions,
  isLoading,
  status,
  error,
  canSendSuggestion,
  onRetryLoad,
  onUseSuggestion,
}) => {
  const { t } = useTranslation('chat');
  const suggestionsByCategory = useMemo(() => {
    const itemsByCategory = new Map<string, PromptSuggestionItem[]>();
    for (const suggestion of suggestions) {
      const categoryItems = itemsByCategory.get(suggestion.category_id) || [];
      categoryItems.push(suggestion);
      itemsByCategory.set(suggestion.category_id, categoryItems);
    }
    return itemsByCategory;
  }, [suggestions]);
  const visibleCategories = categories.filter((category) => (
    suggestionsByCategory.has(category.id)
  ));
  const uncategorizedSuggestions = suggestions.filter((suggestion) => (
    !categories.some((category) => category.id === suggestion.category_id)
  ));
  const handleRetryLoad = () => {
    void Promise.resolve(onRetryLoad()).catch(() => undefined);
  };

  if (status === 'loading' && suggestions.length === 0) {
    return (
      <div className="coach-suggestions-state" role="status" aria-live="polite" aria-busy="true">
        <ThemedSpinner size="md" ariaLabel={t('suggestions.loading')} />
      </div>
    );
  }

  if (status === 'error' && suggestions.length === 0) {
    return (
      <div className="coach-suggestions-state coach-suggestions-error" role="alert">
        <p>{error?.detail || t('suggestions.unavailable')}</p>
        <button type="button" className="coach-suggestions-retry" onClick={handleRetryLoad}>
          {t('common:actions.retry')}
        </button>
      </div>
    );
  }

  if (status === 'empty' || suggestions.length === 0) {
    return <div className="coach-suggestions-state">{t('suggestions.empty')}</div>;
  }

  return (
    <section
      className="sidebar-panel-scroll coach-suggestions-panel"
      aria-label={t('suggestions.ariaLabel')}
    >
      {error && (
        <div className="coach-suggestions-revalidation-error" role="status">
          <span>{error.detail || t('suggestions.unavailable')}</span>
          <button type="button" onClick={handleRetryLoad}>
            {t('common:actions.retry')}
          </button>
        </div>
      )}
      {visibleCategories.map((category) => (
        <section className="coach-suggestions-category" key={category.id}>
          <h3>{category.label}</h3>
          <div className="coach-suggestions-category-list" role="list">
            {suggestionsByCategory.get(category.id)?.map((suggestion) => (
              <div role="listitem" key={`${suggestion.id}:${suggestion.placement.variant}`}>
                <PromptSuggestionControl
                  item={suggestion}
                  presentation="list"
                  disabled={!canSendSuggestion}
                  onSelect={onUseSuggestion}
                />
              </div>
            ))}
          </div>
        </section>
      ))}
      {uncategorizedSuggestions.length > 0 && (
        <div className="coach-suggestions-category-list" role="list">
          {uncategorizedSuggestions.map((suggestion) => (
            <div role="listitem" key={`${suggestion.id}:${suggestion.placement.variant}`}>
              <PromptSuggestionControl
                item={suggestion}
                presentation="list"
                disabled={!canSendSuggestion}
                onSelect={onUseSuggestion}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};